import { ArrowUp, Check, ListOrdered, Loader2, UserX } from 'lucide-react';
import { useMemo, useState } from 'react';

import {
  useSchedulingAvailability,
  useSchedulingResources,
  useSchedulingServices,
  useWaitlist,
  useWaitlistActions,
} from '../../hooks/use-scheduling';
import { useToastStore } from '../../stores/toast-store';

import {
  actionsFor,
  deriveOccupancy,
  hasGroupSlots,
  sortQueue,
  statusLabel,
  tierLabel,
} from './waitlist-adapter';

import type { WaitlistEntryWire } from '../../lib/api-client';
import type { JSX } from 'react';

const DAY_MS = 24 * 60 * 60_000;

function timeLabel(iso: string): string {
  return new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' }).format(
    new Date(iso),
  );
}

function mutationToast(err: unknown, fallback: string): void {
  const msg = err instanceof Error ? err.message : '';
  const is409 = msg.includes('409') || /conflit|version/i.test(msg);
  useToastStore.getState().push({
    variant: 'error',
    title: 'Fila de espera',
    description: is409
      ? 'A fila mudou enquanto você agia — recarregando. Tente de novo.'
      : msg.length > 0
        ? msg
        : fallback,
    durationMs: 6000,
  });
}

/**
 * WaitlistPanel — Frente B Bloco IV PR6 (controle do operador, gated `scheduling-waitlist`).
 *
 * Duas faces sob um serviço escolhido:
 *   1) CAPACIDADE/OCUPAÇÃO por slot de grupo (reusa `remainingCapacity` da disponibilidade +
 *      a capacidade do recurso) — quantas vagas / quantas tomadas.
 *   2) FILA DE ESPERA do serviço, ORDENADA como a promoção a percorre, com as ações do
 *      operador: aprovar/recusar `pending_approval`, elevar prioridade, liberar manual.
 *
 * O fetch é gateado pela cap no AgendaPage (só monta com `scheduling-waitlist`).
 */
export function WaitlistPanel(): JSX.Element {
  const servicesQ = useSchedulingServices();
  const resourcesQ = useSchedulingResources();
  const [serviceId, setServiceId] = useState('');
  const actions = useWaitlistActions();

  const services = servicesQ.data?.data ?? [];
  const effectiveServiceId = serviceId !== '' ? serviceId : (services[0]?.id ?? '');

  const capacityByResource = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of resourcesQ.data?.data ?? []) m.set(r.id, r.capacity);
    return m;
  }, [resourcesQ.data]);
  const resourceName = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of resourcesQ.data?.data ?? []) m.set(r.id, r.name);
    return m;
  }, [resourcesQ.data]);

  // Disponibilidade de HOJE p/ derivar a ocupação dos slots de grupo.
  const todayStartMs = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  }, []);
  const availabilityQ = useSchedulingAvailability(
    {
      serviceOfferingId: effectiveServiceId,
      from: new Date(Math.max(todayStartMs, Date.now())).toISOString(),
      to: new Date(todayStartMs + DAY_MS).toISOString(),
      limit: 60,
    },
    effectiveServiceId !== '',
  );
  const occupancy = useMemo(
    () => deriveOccupancy(availabilityQ.data?.data ?? [], capacityByResource),
    [availabilityQ.data, capacityByResource],
  );
  const groupOccupancy = useMemo(() => occupancy.filter((o) => o.capacity > 1), [occupancy]);

  const waitlistQ = useWaitlist(
    { serviceOfferingId: effectiveServiceId },
    effectiveServiceId !== '',
  );
  const queue = useMemo(() => sortQueue(waitlistQ.data?.data ?? []), [waitlistQ.data]);

  const okToast = (description: string): void => {
    useToastStore
      .getState()
      .push({ variant: 'success', title: 'Fila de espera', description, durationMs: 3500 });
  };

  const onApprove = (e: WaitlistEntryWire): void => {
    actions.approve.mutate(
      { id: e.id, version: e.version },
      { onSuccess: () => okToast('Inscrição liberada para a fila.'), onError: (err) => mutationToast(err, 'Falha ao aprovar.') },
    );
  };
  const onReject = (e: WaitlistEntryWire): void => {
    actions.reject.mutate(
      { id: e.id, version: e.version },
      { onSuccess: () => okToast('Inscrição recusada.'), onError: (err) => mutationToast(err, 'Falha ao recusar.') },
    );
  };
  const onBump = (e: WaitlistEntryWire): void => {
    // Reorder básico: eleva ao topo prático (urgent_high). Spec §8 difere drag-drop fino p/ v2.
    actions.reprioritize.mutate(
      { id: e.id, version: e.version, priorityTier: 'urgent_high' },
      { onSuccess: () => okToast('Prioridade elevada.'), onError: (err) => mutationToast(err, 'Falha ao reordenar.') },
    );
  };
  const onPromote = (e: WaitlistEntryWire): void => {
    // Libera p/ o próximo slot livre derivado da disponibilidade (operador confirma o desfecho).
    const slot = (availabilityQ.data?.data ?? [])[0];
    if (slot === undefined) {
      useToastStore.getState().push({
        variant: 'info',
        title: 'Fila de espera',
        description: 'Sem vaga livre hoje para liberar. Abra um horário antes.',
        durationMs: 5000,
      });
      return;
    }
    const resourceId = e.preferredResourceId ?? slot.resourceIds[0];
    if (resourceId === undefined) return;
    actions.promote.mutate(
      { id: e.id, resourceId, startsAt: slot.startsAt },
      {
        onSuccess: (r) =>
          okToast(
            r.data.booked
              ? 'Vaga agendada para o cliente.'
              : `Vaga ofertada ao cliente (${String(r.data.offeredCount)}).`,
          ),
        onError: (err) => mutationToast(err, 'Falha ao liberar a vaga.'),
      },
    );
  };

  if (servicesQ.isLoading) {
    return <div className="p-4 text-sm text-text-secondary">Carregando fila de espera…</div>;
  }
  if (services.length === 0) {
    return (
      <div className="p-4 text-sm text-text-secondary">
        Nenhum serviço de agenda nativa cadastrado neste tenant.
      </div>
    );
  }

  const busy =
    actions.approve.isPending ||
    actions.reject.isPending ||
    actions.reprioritize.isPending ||
    actions.promote.isPending;

  return (
    <section
      className="rounded-lg border p-4"
      style={{ borderColor: 'var(--border-default)', background: 'var(--bg-surface)' }}
      aria-label="Fila de espera e capacidade"
    >
      <div className="mb-3 flex items-center gap-2">
        <ListOrdered size={16} aria-hidden style={{ color: 'var(--fg-3)' }} />
        <h2 className="text-sm font-semibold">Fila de espera · capacidade</h2>
      </div>

      <label className="mb-3 block">
        <span className="mb-1 block text-xs font-medium" style={{ color: 'var(--fg-3)' }}>
          Serviço
        </span>
        <select
          className="rounded border px-2 py-1 text-sm"
          style={{ background: 'var(--bg-surface-2)', borderColor: 'var(--border-default)' }}
          value={effectiveServiceId}
          onChange={(ev) => setServiceId(ev.target.value)}
        >
          {services.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      </label>

      {/* Capacidade/ocupação por slot de grupo (só quando há grupo) */}
      {hasGroupSlots(occupancy) && (
        <div className="mb-4">
          <div className="mb-2 text-xs font-medium uppercase" style={{ color: 'var(--fg-3)', letterSpacing: 'var(--track-caps)' }}>
            Ocupação dos horários de grupo (hoje)
          </div>
          <div className="flex flex-wrap gap-2" data-testid="waitlist-capacity">
            {groupOccupancy.map((o) => (
              <div
                key={o.startsAt}
                className="rounded border px-3 py-2 text-sm"
                style={{ borderColor: 'var(--border-default)', background: 'var(--bg-surface-2)' }}
                title={o.resourceIds.map((id) => resourceName.get(id) ?? id).join(', ')}
              >
                <span className="font-mono">{timeLabel(o.startsAt)}</span>
                <span className="ml-2 text-xs" style={{ color: 'var(--fg-3)' }}>
                  {o.occupied}/{o.capacity} ocupadas · {o.remaining} {o.remaining === 1 ? 'vaga' : 'vagas'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Lista de espera + ações do operador */}
      <div className="mb-2 text-xs font-medium uppercase" style={{ color: 'var(--fg-3)', letterSpacing: 'var(--track-caps)' }}>
        Lista de espera
      </div>
      {waitlistQ.isLoading && (
        <div className="flex items-center gap-2 p-3 text-sm text-text-secondary">
          <Loader2 size={14} className="animate-spin" aria-hidden /> Carregando a fila…
        </div>
      )}
      {waitlistQ.isError && (
        <div
          className="p-3 text-sm"
          style={{ color: 'var(--color-error, #dc2626)' }}
          role="alert"
          aria-live="polite"
        >
          Não foi possível carregar a fila de espera.
        </div>
      )}
      {!waitlistQ.isLoading && !waitlistQ.isError && queue.length === 0 && (
        <div className="p-3 text-sm text-text-secondary">Ninguém na fila deste serviço.</div>
      )}
      {!waitlistQ.isError && queue.length > 0 && (
        <ol className="flex flex-col gap-2" data-testid="waitlist-queue">
          {queue.map((e, idx) => {
            const acts = actionsFor(e.status);
            return (
              <li
                key={e.id}
                className="flex flex-wrap items-center gap-2 rounded border px-3 py-2 text-sm"
                style={{ borderColor: 'var(--border-default)', background: 'var(--bg-surface-2)' }}
              >
                <span className="font-mono text-xs" style={{ color: 'var(--fg-3)' }}>
                  #{idx + 1}
                </span>
                <span className="font-medium">{e.personName ?? e.personId ?? 'Sem pessoa'}</span>
                <span
                  className="rounded px-1.5 py-0.5 text-xs"
                  style={{ background: 'var(--accent-bg)', color: 'var(--accent-fg)' }}
                >
                  {tierLabel(e.priorityTier)}
                </span>
                <span className="text-xs" style={{ color: 'var(--fg-3)' }}>
                  {statusLabel(e.status)}
                  {e.preferredResourceId !== null
                    ? ` · ${resourceName.get(e.preferredResourceId) ?? e.preferredResourceId}`
                    : ''}
                </span>

                <span className="ml-auto flex items-center gap-1">
                  {acts.canApprove && (
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      disabled={busy}
                      onClick={() => onApprove(e)}
                      title="Liberar inscrição para a fila"
                    >
                      <Check size={14} aria-hidden /> Aprovar
                    </button>
                  )}
                  {acts.canReject && (
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      disabled={busy}
                      onClick={() => onReject(e)}
                      title="Recusar inscrição"
                    >
                      <UserX size={14} aria-hidden /> Recusar
                    </button>
                  )}
                  {acts.canReprioritize && e.priorityTier === 'normal' && (
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      disabled={busy}
                      onClick={() => onBump(e)}
                      title="Elevar prioridade"
                    >
                      <ArrowUp size={14} aria-hidden /> Priorizar
                    </button>
                  )}
                  {acts.canPromote && (
                    <button
                      type="button"
                      className="btn btn--sm"
                      disabled={busy}
                      onClick={() => onPromote(e)}
                      title="Liberar uma vaga para este cliente"
                    >
                      Liberar vaga
                    </button>
                  )}
                </span>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}
